import { checkDate } from "capitalrx-utilities";
import { GeneralTypes } from "@/types";

import {
  ManualClaimFormattedAdditionalMessage,
  ManualClaimFormattedDURMessage,
  ManualClaimFormattedPricingData,
  ManualClaimFormattedResponseData,
  ManualClaimRawResponseData,
  ManualClaimResponseHeaderFields,
  ManualClaimResponsePriceField,
  ManualClaimResponsePricingCategories,
} from "@/types/appTypes/transmissions";

import { ClaimTypes } from "@/types";

type ClaimFields = GeneralTypes.Dictionary<string | number | boolean | Array<number> | Array<string> | Array<object>> | object;

const amountsPaid: Array<ManualClaimResponsePriceField> = [
  {
    label: "Ingredient Cost Paid",
    field: "ingredient_cost_paid",
  },
  {
    label: "Dispensing Fee Paid",
    field: "dispensing_fee_paid",
  },
  {
    label: "Total Amount Paid",
    field: "total_amount_paid",
  },
];

const patientResponsibility: Array<ManualClaimResponsePriceField> = [
  {
    label: "Patient Pay Amount",
    field: "patient_pay_amount",
  },
  {
    label: "Amount of Copay",
    field: "amount_of_copay",
  },
  {
    label: "Amount of Coinsurance",
    field: "amount_of_coinsurance",
  },
];

const accumulatorAmounts: Array<ManualClaimResponsePriceField> = [
  {
    label: "Accumulated Deductible Amount",
    field: "accumulated_deductible_amount",
  },
  {
    label: "Remaining Deductible Amount",
    field: "remaining_deductible_amount",
  },
  {
    label: "Remaining Benefit Amount",
    field: "remaining_benefit_amount",
  },
];

const amountsApplied: Array<ManualClaimResponsePriceField> = [
  {
    label: "Amount Applied to Periodic Deductible",
    field: "amount_applied_to_periodic_deductible",
  },
  {
    label: "Amount Applied to Periodic Maximum Out of Pocket",
    field: "amount_applied_to_periodic_maximum_out_of_pocket",
  },
  {
    label: "Amount Applied to Periodic Maximum Benefit",
    field: "amount_applied_to_periodic_maximum_benefit",
  },
  {
    label: "Amount Exceeding Periodic Benefit Maximum",
    field: "amount_exceeding_periodic_benefit_maximum",
  },
];

const amountsAttributed: Array<ManualClaimResponsePriceField> = [
  {
    label: "Amount Attributed To Product Selection Brand Drug",
    field: "amount_attributed_to_product_selection_brand_drug",
  },
  {
    label: "Amount Attr. to Product Selection Non-Preferred",
    field: "amount_attributed_to_product_selection_non_preferred",
  },
  {
    label: "Amount Attr. to Product Selection Brand Non-Preferred",
    field: "amount_attributed_to_product_selection_brand_non_preferred",
  },
  {
    label: "Amount Attributed to Provider Network Selection",
    field: "amount_attributed_to_provider_network_selection",
  },
  {
    label: "Amount Attributed to Percentage Tax",
    field: "amount_attributed_to_percentage_tax",
  },
  {
    label: "Amount Attributed to Processor Fee",
    field: "amount_attributed_to_processor_fee",
  },
  {
    label: "Amount Attributed to Regulatory Fee",
    field: "amount_attributed_to_regulatory_fee",
  },
];

const taxAmounts: Array<ManualClaimResponsePriceField> = [
  {
    label: "Percentage Tax Amount Paid",
    field: "percentage_tax_amount_paid",
  },
  {
    label: "Patient Percentage Tax Amount",
    field: "patient_percentage_tax_amount",
  },
  {
    label: "Percentage Tax Rate Paid",
    field: "percentage_tax_rate_paid",
  },
];

const otherAmountsPaid: Array<ManualClaimResponsePriceField> = [
  {
    label: "Other Amount Paid #1",
    field: "other_amount_paid_1",
  },
  {
    label: "Other Amount Paid #2",
    field: "other_amount_paid_2",
  },
  {
    label: "Other Amount Paid #3",
    field: "other_amount_paid_3",
  },
  {
    label: "Other Payer Amount Recognized",
    field: "other_payer_amount_recognized",
  },
];

const priceCategories: ManualClaimResponsePricingCategories = {
  amountsPaid,
  patientResponsibility,
  accumulatorAmounts,
  amountsApplied,
  amountsAttributed,
  taxAmounts,
  otherAmountsPaid,
};

const renderedPricingFields = [
  "ingredient_cost_paid",
  "dispensing_fee_paid",
  "total_amount_paid",
  "patient_pay_amount",
  "amount_of_copay",
  "amount_of_coinsurance",
  "accumulated_deductible_amount",
  "remaining_deductible_amount",
  "remaining_benefit_amount",
  "amount_applied_to_periodic_deductible",
  "amount_applied_to_periodic_maximum_benefit",
  "amount_applied_to_periodic_maximum_out_of_pocket",
  "amount_attributed_to_product_selection_brand_drug",
  "amount_attributed_to_percentage_tax",
  "amount_exceeding_periodic_benefit_maximum",
  "amount_attributed_to_product_selection_non_preferred",
  "amount_attributed_to_provider_network_selection",
  "amount_attributed_to_product_selection_brand_non_preferred",
  "amount_attributed_to_coverage_gap",
  "amount_attributed_to_processor_fee",
  "amount_attributed_to_regulatory_fee",
  "percentage_tax_amount_paid",
  "percentage_tax_rate_paid",
  "other_amount_paid_1",
  "other_amount_paid_2",
  "other_amount_paid_3",
  "other_payer_amount_recognized",
];

const renderedHeaderFields = ["id", "source", "transaction_response_status", "service_provider_id", "date_of_service"];

const renderedDURFields = [
  "dur_free_text_message",
  "dur_additional_text",
  "clinical_significance_code",
  "database_indicator",
  "other_pharmacy_indicator",
  "other_prescriber_indicator",
  "previous_date_of_fill",
  "quantity_of_previous_fill",
  "reason_for_service_code",
];

const formatKey = (field: string): string => {
  return field.replace(/_./g, (chunk) => chunk.toUpperCase()[1]);
};

const formatHeaderFields = (headerFields: ClaimFields): ManualClaimResponseHeaderFields => {
  return {
    id: headerFields["id"],
    source: headerFields["source"],
    transactionResponseStatus: headerFields["transaction_response_status"],
    dateOfService: checkDate(headerFields["date_of_service"]?.toString()),
    serviceProviderID: headerFields["service_provider_id"],
  };
};

const formatClaimMessages = (additionalMessageFields: ClaimFields): Array<ManualClaimFormattedAdditionalMessage> => {
  // Concatenate the character limited lines into one message.

  let fieldIndex = 1;
  let finalMessage = "";
  const formattedMessages: Array<ManualClaimFormattedAdditionalMessage> = [];


  Object.entries(additionalMessageFields).forEach((_) => {
    const textFieldKey = `additional_message_information_${fieldIndex}`;
    const continuityFieldKey = `additional_message_information_continuity_${fieldIndex}`;
    fieldIndex++; // safe to advance at this point since keys are already declared.

    // Add current line to full message
    if (additionalMessageFields[textFieldKey]) {
      finalMessage += additionalMessageFields[textFieldKey];
    }

    if (additionalMessageFields[continuityFieldKey]) {
      /* If the continuity character is present, the next line is part of this message.
         Do not clear the message. */
      return;
    }

    if (!additionalMessageFields[continuityFieldKey] && finalMessage !== "") {
      /* The continuity character is not present. This is the last line of this message.
         Add to output and clear the current message. */
      formattedMessages.push({
        message: finalMessage,
      });

      finalMessage = "";
    }
  });

  if (formattedMessages.length < 1) {
    return [];
  }

  return formattedMessages;
};

const formatDURMessages = (durMessageFields: ClaimFields): Array<ManualClaimFormattedDURMessage> => {
  /*
  Formats the DUR Message fields into individual message boxes, filling in N/A where necessary.
  Loop maxes at 3 messages according to NCPDP standard.
  */

  const formattedDURMessages: Array<ManualClaimFormattedDURMessage> = [];
  for (let i = 1; i < 4; i++) {
    const durMessage: ManualClaimFormattedDURMessage = {} as ManualClaimFormattedDURMessage;

    /* Transform field names into flattened format.
       Then check for each flattened key in inputted fields,
       adding its value or N/A to final object after converting to camelCase. */
    for (const field of renderedDURFields) {
      const flattened_field = field + `_${i}`;
      durMessage[formatKey(field)] = flattened_field in durMessageFields ? durMessageFields[flattened_field] : "N/A";
    }

    if (durMessage["durFreeTextMessage"] && durMessage["durFreeTextMessage"] !== "N/A") {
      formattedDURMessages.push(durMessage);
    }
  }

  if (formattedDURMessages.length < 1) {
    return [];
  }

  return formattedDURMessages;
};

const formatPricingData = (pricingFields: ClaimFields): ManualClaimFormattedPricingData => {
  // Format pricing data with either dollar signs or N/A for missing data.
  const returnedFields: ManualClaimFormattedPricingData = {};

  Object.keys(priceCategories).forEach((category) => {
    returnedFields[category] = [];

    priceCategories[category].forEach(({ field, label }) => {
      const value = field in pricingFields ? `$${pricingFields[field]}` : "N/A";

      returnedFields[category].push({
        label,
        value,
      });
    });
  });

  return returnedFields;
};

export const formatManualClaimResponse = (
  rawResponseData: ManualClaimRawResponseData | ClaimTypes.Claim,
): ManualClaimFormattedResponseData => {
  const additionalMessageFields: ClaimFields = {};
  const rejectCodesFields: ClaimFields = {};
  const pricingFields: ClaimFields = {};
  const durFields: ClaimFields = {};
  const headerFields: ClaimFields = {};

  Object.keys(rawResponseData).forEach((field: string) => {
    if (field.match("additional_message_information")) {
      additionalMessageFields[field] = rawResponseData[field];
    }

    if (field.match("reject_code")) {
      rejectCodesFields[field] = rawResponseData[field];
    }

    if (renderedPricingFields.includes(field)) {
      pricingFields[field] = rawResponseData[field];
    }

    if (renderedHeaderFields.includes(field)) {
      headerFields[field] = rawResponseData[field];
    }

    if (renderedDURFields.find((dur) => field.match(dur))) {
      durFields[field] = rawResponseData[field];
    }
  });

  const formattedResponseData: ManualClaimFormattedResponseData = {
    headerFields: {
      id: null,
      source: "test",
      transactionResponseStatus: "",
      serviceProviderID: "",
      dateOfService: "",
    },
    pricingFields: {},
    message: "",
    additionalMessages: [],
    rejectCodes: [],
    durMessages: [],
    reasonsForRejection: [],
    reasonsForAdjudication: [],
    log_key: null,
  };

  if (Object.keys(durFields).length > 0) {
    formattedResponseData["durMessages"] = formatDURMessages(durFields);
  }

  if (rawResponseData.response_message) {
    formattedResponseData["message"] = rawResponseData.response_message;
  }

  if (Object.keys(additionalMessageFields).length > 0) {
    formattedResponseData["additionalMessages"] = formatClaimMessages(additionalMessageFields);
  }

  formattedResponseData["rejectCodes"] = Object.values(rejectCodesFields)
    .filter((code: string) => code !== null)
    .map((code: string) => ({ rejectCode: code }));

  formattedResponseData["pricingFields"] = formatPricingData(pricingFields);
  formattedResponseData["headerFields"] = formatHeaderFields(headerFields);

  if (rawResponseData.reasons_for_rejection) {
    const reasonsForRejectionMessages = [];
    for (const message of (rawResponseData.reasons_for_rejection as Array<string>)) {
      reasonsForRejectionMessages.push({ message: message });
    }
    formattedResponseData.reasonsForRejection = reasonsForRejectionMessages;
  }

  if (rawResponseData.reasons_for_adjudication) {
    const reasonsForAdjudicationMessages = [];
    for (const message of (rawResponseData.reasons_for_adjudication as Array<string>)) {
      reasonsForAdjudicationMessages.push({ message: message });
    }
    formattedResponseData.reasonsForAdjudication = reasonsForAdjudicationMessages;
  }

  if (typeof rawResponseData.log_key === "string") {
    formattedResponseData.log_key = rawResponseData.log_key;
  }

  return formattedResponseData;
};
